import { ref } from "vue";
import type VideoCallsModule from "~/repository/modules/videoCalls";
import type {
  VideoCallRole,
  VideoCallRoomsResponse,
  VideoCallRoomsSaveInput,
  VideoCallRoomsSnapshot,
} from "~/types/videoCalls";
import { getApiErrorMessage, getApiErrorStatus } from "~/utils/apiError";

interface RoomsContext {
  callId: string;
  role: VideoCallRole;
  joinRequestId: string;
  guestSecret: string;
  generation: number;
}

/** Coordinates room intentions without owning the call or its media runtime. */
export function useVideoCallRooms(options: {
  api: VideoCallsModule;
  getContext: () => RoomsContext | null;
  applyMediaSnapshot: (
    payload: VideoCallRoomsResponse,
    runtimeApplied: boolean,
  ) => boolean;
  generateMessageId: () => string;
}) {
  const roomsSnapshot = ref<VideoCallRoomsSnapshot | null>(null);
  const roomsLoading = ref(false);
  const roomsError = ref("");
  const roomsMovingParticipantId = ref<string | null>(null);
  const roomsOpen = ref(false);
  const roomsAnnouncementDismissedId = ref<string | null>(null);
  let operationRevision = 0;
  let announcementAttempt: { body: string; id: string } | null = null;

  const matchesContext = (context: RoomsContext) => {
    const current = options.getContext();
    return (
      current?.callId === context.callId &&
      current.role === context.role &&
      current.generation === context.generation &&
      current.joinRequestId === context.joinRequestId
    );
  };

  const applyRoomsSnapshot = (
    payload: VideoCallRoomsResponse,
    runtimeApplied = false,
  ): boolean => {
    const previous = roomsSnapshot.value;
    if (
      previous &&
      (payload.rooms.revision < previous.revision ||
        payload.rooms.membership_revision < previous.membership_revision)
    )
      return false;
    if (!options.applyMediaSnapshot(payload, runtimeApplied)) return false;
    roomsSnapshot.value = payload.rooms;
    return true;
  };

  const readRooms = (context: RoomsContext) =>
    context.role === "host"
      ? options.api.getRooms(context.callId)
      : options.api.getGuestRooms(context.joinRequestId, context.guestSecret);

  const refreshRooms = async (): Promise<boolean> => {
    const context = options.getContext();
    if (!context || roomsLoading.value) return false;
    const operation = ++operationRevision;
    roomsLoading.value = true;
    roomsError.value = "";
    try {
      const payload = await readRooms(context);
      return matchesContext(context) && applyRoomsSnapshot(payload);
    } catch (error) {
      if (matchesContext(context))
        roomsError.value = getApiErrorMessage(
          error,
          "Não foi possível atualizar as salas.",
        );
      return false;
    } finally {
      if (operation === operationRevision) roomsLoading.value = false;
    }
  };

  const runCommand = async (
    command: (
      context: RoomsContext,
      snapshot: VideoCallRoomsSnapshot,
    ) => Promise<VideoCallRoomsResponse>,
    movingParticipantId: string | null = null,
  ): Promise<boolean> => {
    const context = options.getContext();
    const snapshot = roomsSnapshot.value;
    if (!context || !snapshot || roomsLoading.value) return false;
    const operation = ++operationRevision;
    roomsLoading.value = true;
    roomsMovingParticipantId.value = movingParticipantId;
    roomsError.value = "";
    try {
      const payload = await command(context, snapshot);
      if (!matchesContext(context)) return false;
      // A newer WS snapshot may already have superseded this successful HTTP response.
      applyRoomsSnapshot(payload);
      return true;
    } catch (error) {
      if (!matchesContext(context)) return false;
      if (getApiErrorStatus(error) === 409) {
        try {
          const current = await readRooms(context);
          if (matchesContext(context)) applyRoomsSnapshot(current);
        } catch {
          // Preserve the original actionable conflict instead of hiding it.
        }
      }
      if (matchesContext(context))
        roomsError.value = getApiErrorMessage(
          error,
          "Não foi possível concluir a ação. Tente novamente.",
        );
      return false;
    } finally {
      if (operation === operationRevision) {
        roomsLoading.value = false;
        roomsMovingParticipantId.value = null;
      }
    }
  };

  const saveRooms = (input: VideoCallRoomsSaveInput) =>
    runCommand((context, snapshot) =>
      options.api.saveRooms(context.callId, snapshot.revision, input),
    );
  const openRooms = () =>
    runCommand((context, snapshot) =>
      options.api.openRooms(context.callId, snapshot.revision),
    );
  const closeRooms = () =>
    runCommand((context, snapshot) =>
      options.api.closeRooms(context.callId, snapshot.revision),
    );
  const moveToRoom = (participantId: string, roomId: string) =>
    runCommand(
      (context, snapshot) =>
        options.api.moveToRoom(
          context.callId,
          snapshot.revision,
          participantId,
          roomId,
        ),
      participantId,
    );
  const returnToMainRoom = () =>
    runCommand((context) =>
      options.api.returnToMainRoom(context.joinRequestId, context.guestSecret),
    );
  const requestRoomHelp = () =>
    runCommand((context) =>
      options.api.requestRoomHelp(context.joinRequestId, context.guestSecret),
    );
  const cancelRoomHelp = () =>
    runCommand((context) =>
      options.api.cancelRoomHelp(context.joinRequestId, context.guestSecret),
    );
  const sendRoomAnnouncement = async (value: string) => {
    const body = value.trim();
    if (!body || body.length > 500) {
      roomsError.value = "Escreva um aviso de até 500 caracteres.";
      return false;
    }
    if (announcementAttempt?.body !== body)
      announcementAttempt = { body, id: options.generateMessageId() };
    const messageId = announcementAttempt.id;
    const result = await runCommand((context) =>
      options.api.sendRoomAnnouncement(context.callId, body, messageId),
    );
    if (result) announcementAttempt = null;
    return result;
  };

  const setRoomsOpen = (open: boolean) => {
    roomsOpen.value = open;
  };
  const dismissRoomAnnouncement = () => {
    roomsAnnouncementDismissedId.value =
      roomsSnapshot.value?.announcement?.id ?? null;
  };
  const resetRooms = () => {
    operationRevision += 1;
    roomsSnapshot.value = null;
    roomsOpen.value = false;
    roomsError.value = "";
    roomsLoading.value = false;
    roomsMovingParticipantId.value = null;
    roomsAnnouncementDismissedId.value = null;
    announcementAttempt = null;
  };

  return {
    roomsSnapshot,
    roomsLoading,
    roomsError,
    roomsMovingParticipantId,
    roomsOpen,
    roomsAnnouncementDismissedId,
    applyRoomsSnapshot,
    resetRooms,
    setRoomsOpen,
    refreshRooms,
    saveRooms,
    openRooms,
    closeRooms,
    moveToRoom,
    returnToMainRoom,
    requestRoomHelp,
    cancelRoomHelp,
    sendRoomAnnouncement,
    dismissRoomAnnouncement,
  };
}
